import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Slider,
  FormControlLabel,
  Switch,
  Paper,
  InputAdornment,
  Alert,
} from '@mui/material';
import { LineChart } from '@mui/x-charts';
import { getAnalytics, logEvent } from 'firebase/analytics';

// Function parser and evaluator
const evaluateFunction = (func, x) => {
  // Replace common mathematical functions with their JavaScript equivalents
  const jsFunction = func
    .replace(/sin\(/g, 'Math.sin(')
    .replace(/cos\(/g, 'Math.cos(')
    .replace(/tan\(/g, 'Math.tan(')
    .replace(/log\(/g, 'Math.log10(')
    .replace(/ln\(/g, 'Math.log(')
    .replace(/sqrt\(/g, 'Math.sqrt(')
    .replace(/\^/g, '**')
    .replace(/pi/g, 'Math.PI')
    .replace(/e(?![a-zA-Z])/g, 'Math.E');

  try {
    // Create a function from the string
    const evalFunction = new Function('x', `return ${jsFunction}`);
    return evalFunction(x);
  } catch (error) {
    console.error('Error evaluating function:', error);
    return NaN;
  }
};

const GraphingCalculator = ({ highContrastMode = false, largeButtonsMode = false }) => {
  // State for function input and graph settings
  const [functionInput, setFunctionInput] = useState('x^2');
  const [xMin, setXMin] = useState(-10);
  const [xMax, setXMax] = useState(10);
  const [yMin, setYMin] = useState(-10);
  const [yMax, setYMax] = useState(10);
  const [resolution, setResolution] = useState(100);
  const [autoScale, setAutoScale] = useState(true);
  const [graphData, setGraphData] = useState([]);
  const [error, setError] = useState('');
  const [showGrid, setShowGrid] = useState(true);
  
  // Reference to the chart container for responsive sizing
  const chartContainerRef = useRef(null);
  const [chartDimensions, setChartDimensions] = useState({ width: 500, height: 300 });

  // Update chart dimensions on window resize
  useEffect(() => {
    const updateDimensions = () => {
      if (chartContainerRef.current) {
        const { width } = chartContainerRef.current.getBoundingClientRect();
        setChartDimensions({ width, height: Math.min(width * 0.6, 400) });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Generate graph data points
  const generateGraphData = () => {
    try {
      setError('');
      
      // Validate inputs
      if (xMin >= xMax) {
        setError('X minimum must be less than X maximum');
        return;
      }
      
      if (resolution < 10 || resolution > 1000) {
        setError('Resolution must be between 10 and 1000');
        return;
      }
      
      // Generate data points
      const step = (xMax - xMin) / resolution;
      const data = [];
      let minY = Infinity;
      let maxY = -Infinity;
      
      for (let i = 0; i <= resolution; i++) {
        const x = xMin + step * i;
        const y = evaluateFunction(functionInput, x);
        
        // Skip points that are not finite (e.g., division by zero)
        if (!isFinite(y)) continue;
        
        data.push({ x, y });
        
        // Track min and max y values for auto-scaling
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
      
      // Auto-scale y-axis if enabled
      if (autoScale && data.length > 0) {
        // Add some padding to the y range
        const padding = (maxY - minY) * 0.1;
        setYMin(minY - padding);
        setYMax(maxY + padding);
      }
      
      setGraphData(data);
      
      // Log graph generation to Firebase Analytics
      try {
        const analytics = getAnalytics();
        logEvent(analytics, 'graph_function', {
          calculator_type: 'scientific',
          function: functionInput,
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    } catch (error) {
      setError(`Error generating graph: ${error.message}`);
      console.error('Error generating graph:', error);
    }
  };

  // Generate graph on mount and when inputs change
  useEffect(() => {
    generateGraphData();
  }, [functionInput, xMin, xMax, resolution, autoScale]);

  // Handle function input change
  const handleFunctionChange = (e) => {
    setFunctionInput(e.target.value);
  };

  // Handle range input changes
  const handleRangeChange = (setter) => (e) => {
    setter(parseFloat(e.target.value));
  };

  // Handle resolution slider change
  const handleResolutionChange = (event, newValue) => {
    setResolution(newValue);
  };

  // Handle auto-scale toggle
  const handleAutoScaleChange = (event) => {
    setAutoScale(event.target.checked);
  };

  // Handle grid toggle
  const handleGridChange = (event) => {
    setShowGrid(event.target.checked);
  };

  // Prepare chart data for MUI X-Charts
  const chartData = {
    xAxis: [
      {
        data: graphData.map(point => point.x),
        min: xMin,
        max: xMax,
        label: 'x',
      },
    ],
    series: [
      {
        data: graphData.map(point => point.y),
        label: functionInput,
        color: highContrastMode ? '#90caf9' : '#1976d2',
        showMark: false,
      },
    ],
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Function Graphing
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={2}>
        {/* Function Input */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Function of x"
            value={functionInput}
            onChange={handleFunctionChange}
            placeholder="e.g., x^2, sin(x), 2*x+1"
            variant="outlined"
            helperText="Use x as the variable. Supported functions: sin, cos, tan, log, ln, sqrt. Use ^ for exponents."
            InputProps={{
              sx: { 
                fontSize: largeButtonsMode ? '1.2rem' : '1rem',
                bgcolor: highContrastMode ? '#333' : 'background.paper',
                color: highContrastMode ? 'white' : 'text.primary',
              },
            }}
            sx={{ mb: 2 }}
          />
        </Grid>
        
        {/* X Range */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="X Minimum"
            type="number"
            value={xMin}
            onChange={handleRangeChange(setXMin)}
            InputProps={{
              sx: { 
                bgcolor: highContrastMode ? '#333' : 'background.paper',
                color: highContrastMode ? 'white' : 'text.primary',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="X Maximum"
            type="number"
            value={xMax}
            onChange={handleRangeChange(setXMax)}
            InputProps={{
              sx: { 
                bgcolor: highContrastMode ? '#333' : 'background.paper',
                color: highContrastMode ? 'white' : 'text.primary',
              },
            }}
          />
        </Grid>
        
        {/* Y Range (only if auto-scale is disabled) */}
        {!autoScale && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Y Minimum"
                type="number"
                value={yMin}
                onChange={handleRangeChange(setYMin)}
                InputProps={{
                  sx: { 
                    bgcolor: highContrastMode ? '#333' : 'background.paper',
                    color: highContrastMode ? 'white' : 'text.primary',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Y Maximum"
                type="number"
                value={yMax}
                onChange={handleRangeChange(setYMax)}
                InputProps={{
                  sx: { 
                    bgcolor: highContrastMode ? '#333' : 'background.paper',
                    color: highContrastMode ? 'white' : 'text.primary',
                  },
                }}
              />
            </Grid>
          </>
        )}
        
        {/* Resolution Slider */}
        <Grid item xs={12}>
          <Typography id="resolution-slider" gutterBottom>
            Resolution: {resolution} points
          </Typography>
          <Slider
            value={resolution}
            onChange={handleResolutionChange}
            aria-labelledby="resolution-slider"
            min={10}
            max={500}
            step={10}
            marks={[
              { value: 10, label: '10' },
              { value: 100, label: '100' },
              { value: 250, label: '250' },
              { value: 500, label: '500' },
            ]}
            sx={{
              color: highContrastMode ? '#90caf9' : undefined,
              '& .MuiSlider-thumb': {
                width: largeButtonsMode ? 20 : 12,
                height: largeButtonsMode ? 20 : 12,
              },
            }}
          />
        </Grid>
        
        {/* Options */}
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch 
                checked={autoScale} 
                onChange={handleAutoScaleChange} 
                color="primary" 
              />
            }
            label="Auto-scale Y axis"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch 
                checked={showGrid} 
                onChange={handleGridChange} 
                color="primary" 
              />
            }
            label="Show grid"
          />
        </Grid>
        
        {/* Graph Button */}
        <Grid item xs={12}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={generateGraphData}
            fullWidth
            sx={{
              mb: 2,
              fontSize: largeButtonsMode ? '1.2rem' : '1rem',
              padding: largeButtonsMode ? '12px' : '8px',
            }}
          >
            Graph Function
          </Button>
        </Grid>
        
        {/* Graph Display */}
        <Grid item xs={12}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              bgcolor: highContrastMode ? '#222' : 'background.paper',
              border: highContrastMode ? '1px solid #666' : 'none',
            }}
            ref={chartContainerRef}
          >
            {graphData.length > 0 ? (
              <Box sx={{ height: chartDimensions.height, width: '100%' }}>
                <LineChart
                  xAxis={chartData.xAxis}
                  series={chartData.series}
                  width={chartDimensions.width}
                  height={chartDimensions.height}
                  sx={{
                    '.MuiChartsAxis-label': {
                      fill: highContrastMode ? 'white' : undefined,
                    },
                    '.MuiChartsAxis-tick': {
                      fill: highContrastMode ? 'white' : undefined,
                    },
                    '.MuiChartsAxis-line': {
                      stroke: highContrastMode ? 'white' : undefined,
                    },
                    '.MuiChartsAxis-tickLabel': {
                      fill: highContrastMode ? 'white' : undefined,
                      fontSize: largeButtonsMode ? '1rem' : undefined,
                    },
                  }}
                  grid={{ vertical: showGrid, horizontal: showGrid }}
                  yAxis={[{ min: yMin, max: yMax }]}
                />
              </Box>
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 5 }}>
                No valid data points to display. Please check your function and range.
              </Typography>
            )}
          </Paper>
        </Grid>
        
        {/* Function Examples */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Function Examples:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['x^2', 'sin(x)', 'cos(x)', 'tan(x)', 'log(x)', 'sqrt(x)', '1/x', 'x^3-2*x'].map((example) => (
              <Button 
                key={example} 
                variant="outlined" 
                size="small" 
                onClick={() => setFunctionInput(example)}
                sx={{
                  fontSize: largeButtonsMode ? '1rem' : '0.8rem',
                  bgcolor: highContrastMode ? '#333' : undefined,
                  color: highContrastMode ? 'white' : undefined,
                  borderColor: highContrastMode ? '#666' : undefined,
                  '&:hover': {
                    bgcolor: highContrastMode ? '#444' : undefined,
                    borderColor: highContrastMode ? '#999' : undefined,
                  },
                }}
              >
                {example}
              </Button>
            ))}
          </Box>
        </Grid>
      </Grid>
      
      {/* Keyboard Accessibility Info */}
      <Typography variant="caption" display="block" sx={{ mt: 2 }}>
        <strong>Accessibility:</strong> Use Tab key to navigate between inputs. Press Enter after entering a function to update the graph.
      </Typography>
    </Box>
  );
};

export default GraphingCalculator;
